import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { NewsItem } from "@/types/content";

interface HeroCarouselProps {
  items: NewsItem[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [active, setActive] = useState(0);

  const goNext = useCallback(() => {
    setActive((prev) => (prev + 1) % items.length);
  }, [items.length]);

  const goPrev = () => {
    setActive((prev) => (prev - 1 + items.length) % items.length);
  };

  useEffect(() => {
    const timer = setInterval(goNext, 5000);
    return () => clearInterval(timer);
  }, [goNext]);

  if (items.length === 0) return null;

  return (
    <section className="relative w-full h-[440px] md:h-[560px] overflow-hidden rounded-lg bg-foreground-950">
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === active ? "opacity-100 z-10" : "opacity-0 z-0"
          }`}
        >
          <img
            src={item.cover}
            alt={item.title}
            title={`${item.title} 韩国女主播焦点资讯`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/30 to-foreground-950/20"></div>
          <div className="absolute inset-x-0 bottom-0 p-6 md:p-12 w-full">
            <div className="max-w-3xl">
              <div className="flex items-center gap-2 mb-3">
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-accent-500 text-background-50">
                  焦点专题
                </span>
                <span className="text-background-200/90 text-xs">
                  {item.date} · {item.streamerName}
                </span>
              </div>
              <h2 className="text-2xl md:text-4xl font-heading font-bold text-background-50 leading-tight mb-3">
                {item.title}
              </h2>
              <p className="hidden sm:block text-sm md:text-base text-background-200/90 leading-relaxed line-clamp-2 mb-5">
                {item.summary}
              </p>
              <Link
                to={`/detail/${item.streamerId}`}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                阅读详情
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      ))}

      <button
        type="button"
        aria-label="上一张"
        onClick={goPrev}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-20 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-50/25 text-background-50 hover:bg-background-50/40 backdrop-blur transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={goNext}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-20 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-50/25 text-background-50 hover:bg-background-50/40 backdrop-blur transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute bottom-4 right-6 z-20 hidden md:flex items-center gap-2">
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            aria-label={`切换到第 ${i + 1} 张`}
            onClick={() => setActive(i)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === active ? "w-8 bg-accent-500" : "w-3 bg-background-50/50 hover:bg-background-50/80"
            }`}
          />
        ))}
      </div>
    </section>
  );
}